import { useSyncExternalStore } from "react";

import {
  type ChatScopeFilters,
  type ChatThinkingKind,
  type ChatWsEvent,
  type ClankPublicMessage,
  get_chat_ws_url,
} from "../api";
import { CHAT_MAX_TOTAL_CONTENT_CHARS } from "../api/openapi/chat-limits";
import { demoChatAnswerFor } from "../api/openapi_clients";
import type { ChatMessage, ChatRole } from "../api/types";
import { IS_STATIC_DEMO_FRONTEND } from "../globals";
import { readPersistedChatSessions, writePersistedChatSessions } from "./chatSessionPersistence";
import { createSmoothTextStream } from "./smoothTextStream";

export type ChatEntry =
  /**
   * `filters` is the scope this turn was asked under, kept on user messages so
   * the endpoint can render a mid-conversation scope change as a note on the
   * message that introduced it rather than by rebuilding its system context.
   */
  | { kind: "message"; role: ChatRole; content: string; filters?: ChatScopeFilters }
  | { kind: "thinking"; thinkingKind: ChatThinkingKind }
  | { kind: "scope-note"; label: string };

/**
 * Where the composer stops accepting messages: exactly where the endpoint stops
 * accepting them. Not a number of its own — it is the generated one, so raising
 * or lowering the server's limit moves this with it and the UI can never promise
 * a turn the server will refuse.
 */
export const CHAT_HARD_LIMIT_CHARS = CHAT_MAX_TOTAL_CONTENT_CHARS;

/**
 * Where it starts warning. Derived from the hard limit rather than set, for the
 * same reason: the gap is what gives the user room to finish a thought instead
 * of being cut off mid-conversation, and a gap is only meaningful relative to
 * the limit it precedes.
 */
export const CHAT_SOFT_LIMIT_CHARS = Math.floor(CHAT_HARD_LIMIT_CHARS * 0.6);

/**
 * What the endpoint counts against those limits.
 *
 * The server counts Unicode scalar values and this counts UTF-16 units, so a
 * transcript with astral characters (emoji) counts higher here. That direction
 * is the safe one — the browser stops slightly early rather than waving through
 * a turn the server rejects — and it costs nothing, where counting code points
 * would mean walking the whole transcript on every render.
 */
export const chatTranscriptChars = (session: ChatSession): number =>
  session.entries.reduce(
    (total, entry) => (entry.kind === "message" ? total + entry.content.length : total),
    0,
  );

/**
 * How many messages the endpoint will be asked to accept: the transcript's own,
 * counted the way it counts them — both roles, and nothing that is not a
 * message. Thinking rows and scope notes never leave the browser.
 */
export const chatMessageCount = (session: ChatSession): number =>
  session.entries.reduce((total, entry) => (entry.kind === "message" ? total + 1 : total), 0);

export type ChatStatus = "idle" | "streaming" | "error";

export type ChatSession = {
  entries: ChatEntry[];
  status: ChatStatus;
  error: string | null;
  /** Scope label of the most recently sent message, to detect scope changes. */
  scopeLabel: string | null;
};

const EMPTY_SESSION: ChatSession = { entries: [], status: "idle", error: null, scopeLabel: null };

// Module-level store: chat transcripts survive component unmounts (sidebar
// collapse, tab switches, navigation), and a mirror in session storage carries
// them across a page reload as well.
const sessions = new Map<string, ChatSession>();
const controllers = new Map<string, AbortController>();
const sockets = new Map<string, WebSocket>();
const listeners = new Set<() => void>();

/**
 * Where the transcripts are mirrored so a reload does not discard an answer
 * that took a minute of tool calls to produce.
 *
 * Deliberately `sessionStorage` and not `localStorage`: the panel promises the
 * user that a chat is temporary, and this keeps that true — the transcripts die
 * with the window instead of sitting on disk. It is also a stopgap for the real
 * server-side history, so it stores the least it can get away with: the 20
 * most recently touched chats, independently keyed by process, recording,
 * alert, or Watchlist question. If those do not fit in the browser's quota,
 * the persistence helper evicts the oldest until the remainder does.
 *
 * Versioned, so a transcript written by an older build is simply not found
 * rather than parsed and trusted: `JSON.parse` only throws on malformed text,
 * not on a shape that has since changed underneath it. Bump on any change to
 * `ChatEntry` or `ChatSession`.
 */
/** Keys from oldest to newest; updating an existing chat moves it to the end. */
const sessionRecency = new Map<string, true>();

const touchSession = (key: string) => {
  sessionRecency.delete(key);
  sessionRecency.set(key, true);
};

const persist = () => {
  try {
    writePersistedChatSessions(
      window.sessionStorage,
      [...sessionRecency.keys()].flatMap(key => {
        const session = sessions.get(key);
        return session ? [[key, session] as [string, ChatSession]] : [];
      }),
    );
  } catch {
    // Storage can be unavailable or full. Losing the mirror is not a reason to
    // break the chat that is working perfectly well in memory.
  }
};

const restore = () => {
  for (const [key, session] of readPersistedChatSessions(window.sessionStorage)) {
    // A failure belonged to the page that is gone; the restored transcript
    // opens ready to use rather than showing a dead error.
    sessions.set(key, { ...session, status: "idle", error: null });
    touchSession(key);
  }
};

restore();

const emit = () => {
  for (const listener of listeners) listener();
};

const getSession = (key: string): ChatSession => sessions.get(key) ?? EMPTY_SESSION;

/** A chat's transcript as it stands, for code that reads it outside React. */
export const chatSessionSnapshot = (key: string): ChatSession => getSession(key);

const patchSession = (key: string, patch: Partial<ChatSession>) => {
  const next = { ...getSession(key), ...patch };
  sessions.set(key, next);
  touchSession(key);
  // Writing on every delta would serialize the whole transcript many times a
  // second for an answer that is not final anyway, so streaming turns are
  // mirrored once, when they settle.
  if (next.status !== "streaming") persist();
  emit();
};

/** Replace the content of the trailing assistant message. */
const patchLastAssistant = (key: string, content: string) => {
  const session = getSession(key);
  const entries = [...session.entries];
  const last = entries[entries.length - 1];
  if (last?.kind === "message" && last.role === "assistant") {
    entries[entries.length - 1] = { ...last, content };
    patchSession(key, { entries });
  }
};

const pushThinkingEntry = (key: string, thinkingKind: ChatThinkingKind) => {
  const session = getSession(key);
  const entries = [...session.entries];
  const last = entries[entries.length - 1];
  const entry = { kind: "thinking" as const, thinkingKind };
  if (last?.kind === "message" && last.role === "assistant") {
    entries.splice(entries.length - 1, 0, entry);
  } else {
    entries.push(entry);
  }
  patchSession(key, { entries });
};

const dropThinkingEntries = (key: string) => {
  const session = getSession(key);
  const entries = session.entries.filter(entry => entry.kind !== "thinking");
  if (entries.length !== session.entries.length) {
    patchSession(key, { entries });
  }
};

export const useChatSession = (key: string): ChatSession =>
  useSyncExternalStore(
    callback => {
      listeners.add(callback);
      return () => {
        listeners.delete(callback);
      };
    },
    () => getSession(key),
  );

export type SendChatMessageOptions = {
  /** Scope filters sent alongside the transcript to the unified chat endpoint. */
  filters: ChatScopeFilters;
  /** Human-readable scope label; a change inserts a note into the transcript. */
  scopeLabel: string;
  /** The user's message. */
  text: string;
  /**
   * Background the model should have before the conversation's first message
   * (e.g. the alert result a thread follows up on). Prepended to the first
   * user message of the request only — never shown in the transcript.
   */
  context?: string;
  /** Message shown when the stream fails without a server-provided reason. */
  genericErrorMessage: string;
  /** Formats structured public websocket errors from the backend. */
  formatPublicMessage: (message: ClankPublicMessage) => string;
  /** Message returned by the static demo instead of opening a real chat websocket. */
  staticDemoUnavailableMessage: string;
};

const consumeChatWebSocket = async (
  key: string,
  filters: ChatScopeFilters,
  messages: ChatMessage[],
  signal: AbortSignal,
  genericErrorMessage: string,
  formatPublicMessage: (message: ClankPublicMessage) => string,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const ws = new WebSocket(get_chat_ws_url());
    let settled = false;
    const answer = createSmoothTextStream(content => patchLastAssistant(key, content));
    sockets.set(key, ws);

    const abort = () => {
      settled = true;
      answer.flush();
      ws.close();
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal.addEventListener("abort", abort, { once: true });

    ws.onopen = () => {
      // Every surface reports the viewer's timezone: the backend shows it in
      // the scope context and buckets day/week tools by it instead of UTC.
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      ws.send(JSON.stringify({ messages, timezone, ...filters }));
    };

    ws.onerror = () => {
      settled = true;
      signal.removeEventListener("abort", abort);
      answer.flush();
      reject(new Error(genericErrorMessage));
    };

    ws.onmessage = event => {
      let message: ChatWsEvent;
      try {
        message = JSON.parse(String(event.data)) as ChatWsEvent;
      } catch (err) {
        settled = true;
        signal.removeEventListener("abort", abort);
        answer.flush();
        reject(err);
        ws.close();
        return;
      }

      if (message.type === "thinking") {
        pushThinkingEntry(key, message.kind);
        return;
      }
      if (message.type === "delta") {
        dropThinkingEntries(key);
        answer.push(message.text);
        return;
      }
      if (message.type === "done") {
        settled = true;
        dropThinkingEntries(key);
        void answer.finish().then(() => {
          signal.removeEventListener("abort", abort);
          if (signal.aborted) {
            reject(new DOMException("Aborted", "AbortError"));
            return;
          }
          patchSession(key, { status: "idle" });
          resolve();
          ws.close();
        });
        return;
      }
      if (message.type === "error") {
        settled = true;
        signal.removeEventListener("abort", abort);
        answer.flush();
        dropThinkingEntries(key);
        patchSession(key, {
          status: "error",
          error: message.public_message
            ? formatPublicMessage(message.public_message)
            : message.message || genericErrorMessage,
        });
        resolve();
        ws.close();
        return;
      }
    };

    ws.onclose = () => {
      sockets.delete(key);
      if (!settled) {
        settled = true;
        signal.removeEventListener("abort", abort);
        answer.flush();
        reject(new Error(genericErrorMessage));
      }
    };
  });

export const sendChatMessage = async (
  key: string,
  options: SendChatMessageOptions,
): Promise<void> => {
  const session = getSession(key);
  if (session.status === "streaming") return;

  const entries: ChatEntry[] = [...session.entries];
  const hasMessages = entries.some(entry => entry.kind === "message");
  if (hasMessages && session.scopeLabel !== null && session.scopeLabel !== options.scopeLabel) {
    entries.push({ kind: "scope-note", label: options.scopeLabel });
  }
  entries.push({ kind: "message", role: "user", content: options.text, filters: options.filters });

  // Each user turn carries the scope it was asked under, so the endpoint can
  // tell a scope change from an unchanged one without holding any state of its
  // own. Turns from before this existed have none and read as "unchanged".
  const apiMessages: ChatMessage[] = entries
    .filter(entry => entry.kind === "message")
    .map(entry =>
      entry.role === "user" && entry.filters
        ? { role: entry.role, content: entry.content, scope: entry.filters }
        : { role: entry.role, content: entry.content },
    );
  if (options.context) {
    const first = apiMessages.findIndex(message => message.role === "user");
    if (first !== -1) {
      apiMessages[first] = {
        ...apiMessages[first],
        content: `${options.context}\n\n${apiMessages[first].content}`,
      };
    }
  }

  // Placeholder the deltas stream into.
  entries.push({ kind: "message", role: "assistant", content: "" });
  patchSession(key, {
    entries,
    status: "streaming",
    error: null,
    scopeLabel: options.scopeLabel,
  });

  if (IS_STATIC_DEMO_FRONTEND) {
    // Conversation keys are opaque. Match the question against its actual scope.
    const waitingSession = getSession(key);
    const canned = await demoChatAnswerFor(options.filters, options.text);
    if (getSession(key) !== waitingSession) return;
    if (canned !== null) {
      streamCannedReply(key, canned);
      return;
    }
    patchLastAssistant(key, options.staticDemoUnavailableMessage);
    patchSession(key, { status: "idle" });
    return;
  }

  const controller = new AbortController();
  controllers.set(key, controller);
  try {
    await consumeChatWebSocket(
      key,
      options.filters,
      apiMessages,
      controller.signal,
      options.genericErrorMessage,
      options.formatPublicMessage,
    );
  } catch (err: unknown) {
    // A reset removes its controller before this rejected promise resumes. Do
    // not let the superseded request recreate or alter that session.
    if (controllers.get(key) === controller) {
      if (err instanceof DOMException && err.name === "AbortError") {
        patchSession(key, { status: "idle" });
      } else {
        patchSession(key, {
          status: "error",
          error: err instanceof Error ? err.message : options.genericErrorMessage,
        });
      }
    }
  } finally {
    if (controllers.get(key) === controller) {
      controllers.delete(key);
      dropEmptyTrailingAssistant(key);
    }
  }
};

/** An aborted or failed turn can leave an empty assistant bubble; drop it. */
const dropEmptyTrailingAssistant = (key: string) => {
  const session = getSession(key);
  const entries = session.entries.filter(entry => entry.kind !== "thinking");
  const last = entries[entries.length - 1];
  if (last?.kind === "message" && last.role === "assistant" && last.content === "") {
    patchSession(key, { entries: entries.slice(0, -1) });
  } else if (entries.length !== session.entries.length) {
    patchSession(key, { entries });
  }
};

/**
 * Types a canned answer into the session's waiting assistant bubble at a
 * steady model-like pace: the whole text is queued at once, and the default
 * backlog-proportional reveal would sprint through the bulk and crawl
 * through the tail. No request leaves the browser.
 */
const streamCannedReply = (key: string, answer: string): void => {
  const stream = createSmoothTextStream(streamed => patchLastAssistant(key, streamed), {
    charsPerSecond: 360,
  });
  stream.push(answer);
  void stream.finish().then(() => patchSession(key, { status: "idle" }));
};

/**
 * A guided demo's canned exchange: the question lands as a normal user turn
 * and the prepared answer types itself out through the same smooth stream a
 * live reply uses.
 */
export const playScriptedReply = (key: string, question: string, answer: string): void => {
  const session = getSession(key);
  patchSession(key, {
    entries: [
      ...session.entries,
      { kind: "message", role: "user", content: question },
      { kind: "message", role: "assistant", content: "" },
    ],
    status: "streaming",
    error: null,
  });
  streamCannedReply(key, answer);
};

export const stopChat = (key: string): void => {
  controllers.get(key)?.abort();
  sockets.get(key)?.close();
};

/** Discard the transcript and start fresh, aborting any in-flight answer. */
export const resetChat = (key: string): void => {
  controllers.get(key)?.abort();
  controllers.delete(key);
  sessions.delete(key);
  sessionRecency.delete(key);
  persist();
  emit();
};

export const clearChatError = (key: string): void => {
  const session = getSession(key);
  if (session.status === "error") {
    patchSession(key, { status: "idle", error: null });
  }
};
